import type { InviteResult, KetQuaGuiMail } from '@qlk/shared';
import { useState } from 'react';

import { Nut, ngayGio } from '../../../ui/index.js';

/**
 * Hai khối dùng chung cho mọi chỗ phát link đặt mật khẩu: thẻ người "đã mời",
 * hộp thoại mời, hộp thoại sửa thành viên.
 *
 * Luật: **chưa cấu hình email thì không được nói là đã gửi.** `ThongBaoMail`
 * đọc `mail.trangThai` của máy chủ chứ không tự suy ra, và khi thư không đi thì
 * nó mời quản trị sao chép link ngay tại đó.
 */

export function chuTrangThaiMail(mail: KetQuaGuiMail, email: string): string {
  switch (mail.trangThai) {
    case 'da-gui':
      return `Đã gửi email tới ${email}.`;
    case 'chua-cau-hinh':
      return 'Chưa cấu hình gửi email — hãy sao chép link mời.';
    case 'loi':
      return mail.loi ?? 'Gửi email thất bại — hãy sao chép link mời.';
    case 'khong-gui':
      return 'Chưa gửi email. Sao chép link ở dưới, hoặc bấm "Gửi lại email mời".';
  }
}

/** Màu khối theo trạng thái: xanh khi đi được, đỏ khi lỗi, xám khi chưa gửi. */
function lopTheoTrangThai(trangThai: KetQuaGuiMail['trangThai']): string {
  if (trangThai === 'da-gui') return 'border-ok-chu bg-ok-nen text-ok-chu';
  if (trangThai === 'loi') return 'border-bad-chu bg-bad-nen text-bad-chu';
  return 'border-vien bg-nen text-chu-phu';
}

export interface ThongBaoMailProps {
  ketQua: InviteResult;
  /** Chữ in trên dòng đầu, vd. "Đã tạo lời mời cho an@wmmedia.net". */
  tieuDe: string;
}

/** Kết quả gửi thư + ô link để sao chép. */
export function ThongBaoMail({ ketQua, tieuDe }: ThongBaoMailProps) {
  const loi = ketQua.mail.trangThai === 'loi';

  return (
    <div
      role={loi ? 'alert' : 'status'}
      className={`mt-3 rounded-[10px] border p-3 text-phu ${lopTheoTrangThai(ketQua.mail.trangThai)}`}
    >
      <p className="font-semibold">{tieuDe}</p>
      <p className="mt-1">{chuTrangThaiMail(ketQua.mail, ketQua.email)}</p>
      <p className="mt-1">Link hết hạn {ngayGio(ketQua.expiresAt)}.</p>

      {ketQua.inviteUrl && <OLinkMoi url={ketQua.inviteUrl} />}
    </div>
  );
}

/** Ô chữ chỉ đọc + nút "Sao chép link mời". */
export function OLinkMoi({ url }: { url: string }) {
  const [daChep, datDaChep] = useState(false);

  const saoChep = async () => {
    try {
      await navigator.clipboard.writeText(url);
      datDaChep(true);
    } catch {
      // Trình duyệt chặn clipboard (trang không HTTPS, thiếu quyền): ô chữ vẫn
      // chọn được bằng tay, nên đừng chặn gì — chỉ thôi không báo "đã chép".
      datDaChep(false);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <input
        readOnly
        value={url}
        aria-label="Link đặt mật khẩu"
        onFocus={(e) => {
          e.currentTarget.select();
        }}
        className="so-lieu min-w-0 flex-1 rounded-[8px] border border-vien bg-be-mat px-2 py-1 text-phu text-chu"
      />
      <Nut
        kieu="phu"
        onClick={() => {
          void saoChep();
        }}
      >
        {daChep ? 'Đã sao chép' : 'Sao chép link mời'}
      </Nut>
    </div>
  );
}
